// Normalized-position <-> value conversion for controls, honouring linear vs
// log taper. A knob/slider works in [0,1]; this maps to the param's real range.
// Pure + unit-tested. Log taper needs a positive min; otherwise it degrades to
// linear (so params like glide with min 0 still behave).

export type Taper = 'linear' | 'log';

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

function logUsable(min: number, max: number): boolean {
  return min > 0 && max > 0;
}

/** Map normalized n in [0,1] to a value in [min,max]. */
export function normToValue(
  n: number,
  min: number,
  max: number,
  taper: Taper = 'linear',
  step = 0,
): number {
  const t = clamp(n, 0, 1);
  let v: number;
  if (taper === 'log' && logUsable(min, max)) {
    v = min * Math.pow(max / min, t);
  } else {
    v = min + (max - min) * t;
  }
  if (step > 0) v = Math.round(v / step) * step;
  v = clamp(v, min, max);
  return v === 0 ? 0 : v; // normalize -0
}

/** Inverse of normToValue: value -> normalized n in [0,1]. */
export function valueToNorm(
  value: number,
  min: number,
  max: number,
  taper: Taper = 'linear',
): number {
  const v = clamp(value, min, max);
  if (taper === 'log' && logUsable(min, max)) {
    return Math.log(v / min) / Math.log(max / min);
  }
  return max === min ? 0 : (v - min) / (max - min);
}

/** Human-readable value for a control readout. */
export function formatValue(value: number, unit: string): string {
  let num: string;
  const abs = Math.abs(value);
  if (abs !== 0 && abs < 1) num = value.toFixed(abs < 0.1 ? 3 : 2);
  else if (abs < 100) num = value.toFixed(abs < 10 ? 2 : 1);
  else num = Math.round(value).toString();
  // trim trailing zeros
  if (num.includes('.')) num = num.replace(/\.?0+$/, '');
  return unit ? `${num}${unit === 'cents' || unit === 'bpm' ? ' ' : ''}${unit}` : num;
}
